import qs from 'qs';
import type { operations } from '~/clients/svc-connection-google/models';

import { json_headers, request } from '~/services/common';

type ListPropertiesQuery = operations['listProperties']['parameters']['query'];
type ListPropertiesResponse = operations['listProperties']['responses']['200']['content']['application/json'];
type GHAPropertyResponseRow = ListPropertiesResponse['data'][number];
export type GHAProperty = GHAPropertyResponseRow;

// 'no_tier' | 'tier_1'..'tier_4'; 'no_tier' is the default (untiered).
export type CugTier = GHAProperty['cug_tier'];

export type GHAPropertyBrand = NonNullable<ListPropertiesQuery>['brand'];
export type GHAAccountType = NonNullable<ListPropertiesQuery>['accountType'];

type ListGHADiscountRulesResponse =
  operations['listGHADiscountRules']['responses']['200']['content']['application/json'];
export type GHADiscountRule = ListGHADiscountRulesResponse['result'][number];
export type GHADiscountRuleDiscountType = GHADiscountRule['discountType'];
export type GHADiscountRuleAccountType = NonNullable<
  operations['listGHADiscountRules']['parameters']['query']
>['accountType'];
export type CreateGHADiscountRuleBody =
  operations['createGHADiscountRule']['requestBody']['content']['application/json'];
export type CreateGHADiscountRuleDiscountType = NonNullable<CreateGHADiscountRuleBody['discountType']>;
export type GenerateGHADiscountTokenBody =
  operations['generateGHADiscountTokenDebug']['requestBody']['content']['application/json'];
type BulkUpdateGHADiscountRulesBody =
  operations['bulkUpdateGHADiscountRules']['requestBody']['content']['application/json'];
export type BulkUpdateGHADiscountRulesAccountType = BulkUpdateGHADiscountRulesBody['accountType'];
export type BulkUpdateGHADiscountRulesParams = BulkUpdateGHADiscountRulesBody & { file: File };
export type BulkUpdateGHADiscountRulesResponse =
  operations['bulkUpdateGHADiscountRules']['responses']['200']['content']['application/json'];
export type BulkUpdateGHADiscountRulesErrorResponse =
  operations['bulkUpdateGHADiscountRules']['responses']['400']['content']['application/json'];
export interface ListGHADiscountRulesFilters {
  propertyId?: string;
  accountType?: GHADiscountRuleAccountType;
  enabled?: boolean;
  country?: string;
  page?: number;
  limit?: number;
}

// Property List Regeneration types
type GeneratePropertyListXmlRequest =
  operations['/api/connections/google/pull/properties/generate/put']['requestBody']['content']['application/json'];
export type GHAPropertyListAccountType = GeneratePropertyListXmlRequest['accountType'];

// Property List Metadata types
type GetPropertyListMetadataQuery = operations['getPropertyListMetadata']['parameters']['query'];
type GetPropertyListMetadataResponse =
  operations['getPropertyListMetadata']['responses']['200']['content']['application/json'];
export type PropertyListMetadata = GetPropertyListMetadataResponse['result'][number];

interface PriceComponent {
  price: number;
  supplierCode: string;
  roomID?: string;
}

interface Mismatch {
  isMismatch: boolean;
  reason: string;
  difference: number;
}

export interface GHAPriceCheckRow {
  propertyId: string;
  checkIn: string;
  checkOut: string;
  occupancy: string;
  publicOffer: PriceComponent;
  metaSearch: PriceComponent;
  mismatch: Mismatch;
}

export interface GHAPriceSummary {
  mismatchCount: number;
  totalItineraries: number;
  mismatchPercentage: number;
}

interface GHAPriceDiagnoseResponse {
  results: Array<GHAPriceCheckRow>;
  summary: GHAPriceSummary;
}

export enum BulkSwitchGHAPropertiesAction {
  ENABLE = 'enable',
  DISABLE = 'disable',
}

function getBaseURL() {
  return window.configs.API_HOST + '/api';
}

export async function ghaPriceDiagnoseQuery({
  propertyIds,
  checkIn,
  nights,
  isMobile,
  combinations,
  occupancy,
}: {
  propertyIds: string;
  checkIn: string;
  nights: number;
  isMobile: boolean;
  combinations: number;
  occupancy: number;
}): Promise<{ result: GHAPriceDiagnoseResponse }> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/meta-search/query/diagnose?`;

  const body = {
    propertyIds: propertyIds.length > 0 ? propertyIds.split(',') : [],
    checkIn,
    nights,
    isMobile,
    combinations,
    occupancy,
  };

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

export async function ghaUploadNewProperty(file: File) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties`;

  const formData = new FormData();
  formData.append('file', file);

  return request(url, {
    method: 'POST',
    body: formData,
    headers: {
      Accept: 'application/json',
    },
  });
}

export enum ChannelManager {
  TRAVELCLICK = 'travelclick',
  SITEMINDER = 'siteminder',
  SELF_MANAGED = 'self-managed',
  BEDBANK = 'bedbank',
  DERBYSOFT = 'derbysoft',
  SYNXIS = 'synxis',
}

export enum GHAPropertyType {
  VACATION_RENTAL = 'vacation_rental',
  HOTEL = 'hotel',
}

// Account types for property list generation by brand
export enum LEPropertyListAccountType {
  LPP = 'lpp',
  LE_VACATION_RENTAL = 'le_vacation_rental',
}

export enum ScooponPropertyListAccountType {
  SCOOPON = 'scoopon',
}

export interface SearchGHAPropertiesResponse {
  results: Array<GHAProperty>;
  total: number;
}

export interface SearchGHAPropertiesParams {
  propertyIds: Array<string>;
  liveOnGoogle?: boolean;
  accountType?: GHAAccountType;
  blacklisted?: boolean;
  discountConfigured?: boolean;
  discountBlacklisted?: boolean;
  channelManagers: Array<ChannelManager>;
  page: number;
  limit: number;
  name?: string;
  countryCodes?: Array<string>;
  ghaPropertyTypes?: Array<GHAPropertyType>;
  brand?: GHAPropertyBrand;
  distanceGroups?: Array<string>;
  chain?: string;
  cugTiers?: Array<CugTier>;
}

export async function searchGHAProperties({
  propertyIds,
  liveOnGoogle,
  accountType,
  blacklisted,
  discountConfigured,
  discountBlacklisted,
  channelManagers,
  name,
  countryCodes,
  ghaPropertyTypes,
  brand,
  distanceGroups,
  page,
  limit,
  chain,
  cugTiers,
}: SearchGHAPropertiesParams): Promise<SearchGHAPropertiesResponse> {
  const baseURL = getBaseURL();
  const queryParams = {
    propertyIds,
    channelManagers,
    page: page.toString(),
    limit: limit.toString(),
    liveOnGoogle: liveOnGoogle !== undefined ? liveOnGoogle.toString() : undefined,
    accountType,
    blacklisted: blacklisted !== undefined ? blacklisted.toString() : undefined,
    discountConfigured: discountConfigured !== undefined ? discountConfigured.toString() : undefined,
    discountBlacklisted: discountBlacklisted !== undefined ? discountBlacklisted.toString() : undefined,
    name,
    countryCodes,
    ghaPropertyTypes,
    brand,
    distanceGroups,
    chain,
    cugTiers,
  };

  // 'repeat' => repeated keys (cugTiers=tier_1&cugTiers=tier_4) per backend contract
  const url = `${baseURL}/connections/google/listProperties?${qs.stringify(queryParams, {
    arrayFormat: 'repeat',
    skipNulls: true,
  })}`;

  const response = await request(url, {
    method: 'GET',
  });

  return {
    results: response.data || [],
    total: response.total || 0,
  };
}

export async function disableGHAProperty(propertyIds: Array<string>, accountType: GHAAccountType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/disable`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds, accountType }),
  });
}

export async function enableGHAProperty(propertyIds: Array<string>, accountType: GHAAccountType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/enable`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds, accountType }),
  });
}

interface BulkSwitchGHAPropertiesParams {
  file: File;
  action: BulkSwitchGHAPropertiesAction;
  accountType: GHAAccountType;
}

export async function bulkSwitchGHAProperties({ file, action, accountType }: BulkSwitchGHAPropertiesParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/${action}`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountType', accountType);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

interface UpdatePropertiesParams {
  propertyIds: Array<string>;
  field: string;
  value: boolean;
}
interface BulkAppendPricingRegionsParams {
  file: File;
  accountType: GHAAccountType;
}

export async function bulkAppendPricingRegions({ file, accountType }: BulkAppendPricingRegionsParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/pricing-regions`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountType', accountType);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

export async function bulkUpdateGHADiscountRules({
  file,
  accountType,
}: BulkUpdateGHADiscountRulesParams): Promise<BulkUpdateGHADiscountRulesResponse> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/discount-rules/bulk`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountType', accountType);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

export async function updatePropertyPricingRegions(propertyIds: Array<string>, pricingRegions: Array<string>) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties`;

  const body = propertyIds.map((id) => ({
    id,
    pricing_regions: pricingRegions,
  }));

  return request(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    ignoreResponse: true,
  });
}

export async function updateProperties(props: UpdatePropertiesParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties`;

  const body = props.propertyIds.map((id) => ({
    id,
    [props.field]: props.value,
  }));

  return request(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    ignoreResponse: true,
  });
}

export async function getGHAAggregateReport({
  aggregateNumber,
  sortBy,
  sortOrder,
  page,
  limit,
}: {
  aggregateNumber: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
}): Promise<{ result; count: number }> {
  const queryParams = {
    sortBy,
    sortOrder,
    aggregateNumber,
    page,
    limit,
  };
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/reports/aggregate-report?${qs.stringify(queryParams)}`;
  return request(url, {
    method: 'GET',
  });
}

export function downloadRecentPricingReport() {
  const baseURL = getBaseURL();
  const downloadUrl = `${baseURL}/connections/google/reports/pricing/all`;
  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

export function downloadGHAAggregateReportCSV({
  aggregateNumber,
  sortBy,
  sortOrder,
}: {
  aggregateNumber: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}) {
  const queryParams = {
    sortBy,
    sortOrder,
    aggregateNumber,
  };
  const baseURL = getBaseURL();
  const downloadUrl = `${baseURL}/connections/google/reports/aggregate-report.csv?${qs.stringify(queryParams)}`;
  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

export function downloadGHARawReportCSV({
  propertyIds,
  createdAtStart,
  createdAtEnd,
  limit,
}: {
  propertyIds?: Array<string>;
  createdAtStart?: string;
  createdAtEnd?: string;
  limit?: string;
}) {
  const queryParams = {
    propertyId: propertyIds && propertyIds.length > 0 ? propertyIds : undefined,
    createdAtStart,
    createdAtEnd,
    limit,
  };
  const queryString = qs.stringify(queryParams, { arrayFormat: 'repeat', skipNulls: true });
  const baseURL = getBaseURL();
  const downloadUrl = `${baseURL}/connections/google/reports/report.csv?${queryString}`;

  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

export function downloadGHAPropertiesCSV(params: Omit<SearchGHAPropertiesParams, 'page' | 'limit'>) {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true });
  const downloadUrl = `${baseURL}/connections/google/properties/export/csv?${queryString}`;
  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

export async function enableGHPriceTracking(propertyIds: Array<string>) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/price-tracking/enable`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds }),
  });
}

export async function disableGHPriceTracking(propertyIds: Array<string>) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/price-tracking/disable`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds }),
  });
}

export async function updateGHAPropertyType(propertyIds: Array<string>, ghaPropertyType: GHAPropertyType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/update/gha-property-type`;

  return request(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds, ghaPropertyType }),
  });
}

export async function bulkUpdateGHAPropertyType(file: File, ghaPropertyType: GHAPropertyType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/update/gha-property-type`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('ghaPropertyType', ghaPropertyType);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

export enum BlacklistAction {
  BLACKLIST = 'blacklist',
  UNBLACKLIST = 'unblacklist',
}

export async function updatePropertyBlacklist(propertyIds: Array<string>, action: BlacklistAction) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/blacklist/${action}`;

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyIds }),
  });
}

interface BulkBlacklistPropertiesParams {
  file: File;
  action: BlacklistAction;
}

export async function bulkBlacklistProperties({ file, action }: BulkBlacklistPropertiesParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/blacklist/${action}`;

  const formData = new FormData();
  formData.append('file', file);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

export enum CUGWhitelistAction {
  WHITELIST = 'whitelist',
  UNWHITELIST = 'unwhitelist',
}

interface BulkCUGWhitelistPropertiesParams {
  file: File;
  action: CUGWhitelistAction;
}

export async function bulkCUGWhitelistProperties({ file, action }: BulkCUGWhitelistPropertiesParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/cug-whitelist`;

  const whitelisted = action === CUGWhitelistAction.WHITELIST;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('whitelisted', whitelisted.toString());

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

// CUG Tier: JSON PUT for selected property ids, multipart CSV POST for bulk uploads.
interface SetCugTierParams {
  propertyIds: Array<string>;
  tier: CugTier;
}

export async function setCugTier({ propertyIds, tier }: SetCugTierParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/cug-tier`;

  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ propertyIds, tier }),
  });
}

interface BulkSetCugTierParams {
  file: File;
  // Tier applied to every row in the CSV.
  tier: CugTier;
}

export async function bulkSetCugTier({ file, tier }: BulkSetCugTierParams) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/properties/cug-tier`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('tier', tier);

  return request(url, {
    method: 'POST',
    body: formData,
  });
}

// Property List Regeneration
export async function generatePropertyListXml(accountType: GHAPropertyListAccountType) {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/pull/properties/generate`;

  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ accountType }),
  });
}

// Property List Metadata
export async function getPropertyListMetadata(
  params?: GetPropertyListMetadataQuery,
): Promise<GetPropertyListMetadataResponse> {
  const baseURL = getBaseURL();
  const queryString = params ? `?${qs.stringify(params)}` : '';
  const url = `${baseURL}/connections/google/properties/list/metadata${queryString}`;

  return request(url, {
    method: 'GET',
  });
}

export async function listGHADiscountRules(
  filters: ListGHADiscountRulesFilters = {},
  options?: { signal?: AbortSignal },
): Promise<ListGHADiscountRulesResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(
    {
      ...filters,
      enabled: filters.enabled === undefined ? undefined : String(filters.enabled),
    },
    { skipNulls: true },
  );
  const url = `${baseURL}/connections/google/discount-rules${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

export async function createGHADiscountRule(
  input: CreateGHADiscountRuleBody,
): Promise<operations['createGHADiscountRule']['responses']['201']['content']['application/json']> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/discount-rules`;

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function generateGHADiscountTokenDebug(
  input: GenerateGHADiscountTokenBody,
): Promise<operations['generateGHADiscountTokenDebug']['responses']['200']['content']['application/json']> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/debug/generate-discount-token`;

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(input),
  });
}

export async function archiveGHADiscountRule(
  id: string,
): Promise<operations['archiveGHADiscountRule']['responses']['200']['content']['application/json']> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/discount-rules/${id}/archive`;

  return request(url, {
    method: 'PATCH',
    headers: json_headers,
  });
}

type HintCoverageMatrixResponse =
  operations['getHintCoverageMatrix']['responses']['200']['content']['application/json'];
export type HintCoverageMatrixResult = HintCoverageMatrixResponse['result'];
export type HintCoverageMatrixCell = HintCoverageMatrixResult['matrix'][number];
export type HintCoverageMatrixParams = Partial<operations['getHintCoverageMatrix']['parameters']['query']> &
  Pick<operations['getHintCoverageMatrix']['parameters']['query'], 'accountType'>;

export async function getHintCoverageMatrix(
  params: HintCoverageMatrixParams,
): Promise<{ result: HintCoverageMatrixResult }> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  const url = `${baseURL}/connections/google/reports/hint-coverage?${queryString}`;

  return request(url, {
    method: 'GET',
  });
}

type HintCoverageGridResponse = operations['getHintCoverageGrid']['responses']['200']['content']['application/json'];
export type HintCoverageGridResult = HintCoverageGridResponse['result'];
export type HintCoverageGridCell = HintCoverageGridResult['cells'][number];
export type HintCoverageAccountType = HintCoverageMatrixParams['accountType'];
export type HintCoverageGridParams = operations['getHintCoverageGrid']['parameters']['query'];

export async function getHintCoverageGrid(
  params: HintCoverageGridParams,
  options?: { signal?: AbortSignal },
): Promise<{ result: HintCoverageGridResult }> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  const url = `${baseURL}/connections/google/reports/hint-coverage/buckets?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

type AllocatePropertiesForHintsResponse =
  operations['allocatePropertiesForHints']['responses']['202']['content']['application/json'];

export async function allocateHintProperties(
  accountType: HintCoverageAccountType,
): Promise<AllocatePropertiesForHintsResponse> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/hints/allocate-properties`;

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ accountType }),
  });
}

type ListHintPropertiesResponse = operations['listHintProperties']['responses']['200']['content']['application/json'];
export type AllocatedProperty = ListHintPropertiesResponse['data'][number];
export type AllocatedPropertyTier = AllocatedProperty['tier'];
export type GetAllocatedPropertiesParams = operations['listHintProperties']['parameters']['query'];

export async function getHintAllocatedProperties(
  params: GetAllocatedPropertiesParams,
  options?: { signal?: AbortSignal },
): Promise<ListHintPropertiesResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify({ ...params, search: params.search || undefined }, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/properties?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

type PropertyHintCoverageResponse =
  operations['getPropertyHintCoverage']['responses']['200']['content']['application/json'];
export type PropertyHintCoverageRow = PropertyHintCoverageResponse['rows'][number];
export type GetPropertyHintCoverageParams = operations['getPropertyHintCoverage']['parameters']['query'];

export async function getPropertyHintCoverage(
  propertyId: string,
  params: GetPropertyHintCoverageParams,
  options?: { signal?: AbortSignal },
): Promise<PropertyHintCoverageResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/properties/${propertyId}/coverage?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

export type ExportHintPropertiesCSVParams = operations['exportHintPropertiesCSV']['parameters']['query'];

export function downloadHintAllocatedPropertiesCSV(params: ExportHintPropertiesCSVParams) {
  const baseURL = getBaseURL();
  const queryString = qs.stringify(params, { skipNulls: true });
  const downloadUrl = `${baseURL}/connections/google/hints/properties/export/csv?${queryString}`;

  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

type ListHintRegionSettingsResponse =
  operations['listHintRegionSettings']['responses']['200']['content']['application/json'];
export type HintRegionSetting = ListHintRegionSettingsResponse['result'][number];
export type UpsertHintRegionSettingBody =
  operations['upsertHintRegionSetting']['requestBody']['content']['application/json'];
type UpsertHintRegionSettingResponse =
  operations['upsertHintRegionSetting']['responses']['200']['content']['application/json'];

export async function listHintRegionSettings(
  accountType: HintCoverageAccountType,
  options?: { signal?: AbortSignal },
): Promise<ListHintRegionSettingsResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify({ accountType }, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/region-settings?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

// Persist-only: the backend queues nothing on save — a settings change takes effect at
// the next weekly reclassification cron or a Re-classify Properties click.
export async function upsertHintRegionSetting(
  body: UpsertHintRegionSettingBody,
): Promise<UpsertHintRegionSettingResponse> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/connections/google/hints/region-settings`;

  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

type HintRegionCompositionResponse =
  operations['getHintRegionComposition']['responses']['200']['content']['application/json'];
export type HintRegionComposition = HintRegionCompositionResponse['result'];
export type HintRegionCompositionRow = HintRegionComposition['regions'][number];

export async function getHintRegionComposition(
  accountType: HintCoverageAccountType,
  options?: { signal?: AbortSignal },
): Promise<HintRegionCompositionResponse> {
  const baseURL = getBaseURL();
  const queryString = qs.stringify({ accountType }, { skipNulls: true });
  const url = `${baseURL}/connections/google/hints/region-composition?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}

type ListHintClassificationChangesResponse =
  operations['listHintClassificationChanges']['responses']['200']['content']['application/json'];
export type ClassificationChange = ListHintClassificationChangesResponse['data'][number];
export type ClassificationChangeTier = ClassificationChange['oldTier'];
export type GetClassificationChangesParams = operations['listHintClassificationChanges']['parameters']['query'];

export async function getHintClassificationChanges(
  params: GetClassificationChangesParams,
  options?: { signal?: AbortSignal },
): Promise<ListHintClassificationChangesResponse> {
  const baseURL = getBaseURL();
  // 'repeat' => repeated keys (oldTier=HIGH&oldTier=MEDIUM) per backend contract, same as
  // cugTiers on listProperties. The default indexed form is silently ignored by the API.
  const queryString = qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true });
  const url = `${baseURL}/connections/google/hints/classification-changes?${queryString}`;

  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
}
